import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const FAQSection = () => {
  const faqs = [
    {
      question: "How does Dollar Bill integrate with my existing systems?",
      answer:
        "We integrate with Toast, Square, Clover, Uber Eats, DoorDash, and Grubhub. Connect your accounts and we automatically sync all your sales data — no manual entry needed.",
    },
    {
      question: "Do I need accounting experience to use Dollar Bill?",
      answer:
        "Not at all. Dollar Bill is designed for restaurant owners, not accountants. Bill explains everything in plain English. You ask a question, you get a clear answer with specific actions.",
    },
    {
      question: "How secure is my financial data?",
      answer:
        "We use bank-level 256-bit encryption for all data. Your credentials are never stored — we use secure OAuth connections. Your data is yours.",
    },
    {
      question: "Can I forward receipts via email?",
      answer:
        "Yes. Each company gets a unique email address. Forward receipts and our system automatically extracts prices, quantities, and categories — no manual entry.",
    },
    {
      question: "What kind of reports can I generate?",
      answer:
        "Weekly P&L statements, cash flow reports, menu profitability analysis, delivery channel comparison, vendor cost tracking, and tax documents. All exportable to PDF or Excel.",
    },
    {
      question: "How long does setup take?",
      answer:
        "Most operators are fully set up in under 15 minutes. Connect your POS, link your bank account, and you're ready. Our onboarding guide walks you through each step.",
    },
    {
      question: "How do I get started?",
      answer:
        "Reach out to us at hello@askdollarbill.com or use our contact page. We'll walk you through the onboarding process personally — connecting your POS, bank account, and setting up your first weekly P&L.",
    },
  ];

  return (
    <section id="faq" className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-midnight tracking-tight">
            Frequently asked{" "}
            <span className="text-vault-green">questions</span>
          </h2>
          <p className="text-lg text-slate-brand">
            Everything you need to know about Dollar Bill.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.04 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="bg-stone border border-fog rounded-xl px-6 hover:border-vault-green/30 transition-colors data-[state=open]:border-vault-green/40 data-[state=open]:shadow-sm"
                >
                  <AccordionTrigger className="text-left font-semibold text-midnight hover:text-vault-green py-5 hover:no-underline text-sm">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-brand pb-5 leading-relaxed text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-10 text-center"
        >
          <p className="text-slate-brand text-sm mb-2">Still have questions?</p>
          <a
            href="mailto:hello@askdollarbill.com"
            className="inline-flex items-center gap-2 text-vault-green font-semibold text-sm hover:text-vault-green/80 transition-colors"
          >
            Email hello@askdollarbill.com →
          </a>
        </motion.div>
      </div>
    </section>
  );
};
